import {
  AllCommentsProps,
  BusniessOfficeNote,
} from "../../../data-model/busniessOfficeNote";
import { BusniessOfficeReportType } from "../../../enums/busniessOfficeReportType";
import { createBusniessOfficePostNotes } from "../../singleOrderLayout/utils/createBusniessOfficeNoteData";

/**
 * Represents the structure of a business office report for inactive insurance.
 */
interface InactiveInsuranceReport extends Pick<
  AllCommentsProps,
  "comments" | "commentDate" | "type"
> {}

/**
 * Generates a business office report specifically for inactive insurance cases.
 * Transforms raw business office notes into a structured report format
 * suitable for displaying inactive insurance information.
 *
 * @param {BusniessOfficeNote[]} [reports] - Array of business office notes to process.
 * @param {string} [boStatus] - The business office status (passed as dateOfService to post notes).
 * @param {string} [comment] - Optional comment text to use as the write-back template.
 * @returns {InactiveInsuranceReport | null} The generated report containing comments,
 *                                           comment date, and type. Returns null if
 *                                           no valid comments are found.
 *
 * @example
 * // With valid reports
 * const report = generateBusniessOfficeReportForInactiveInsurance(
 *   reports,
 *   "pending",
 *   "Insurance is inactive"
 * );
 * // Returns: { comments: [...], commentDate: "2024-01-15", type: "post" }
 *
 * @example
 * // With empty or undefined reports
 * const report = generateBusniessOfficeReportForInactiveInsurance(undefined);
 * // Returns: null
 */
export const generateBusniessOfficeReportForInactiveInsurance = (
  reports?: BusniessOfficeNote[],
  comment?: string,
): InactiveInsuranceReport | null => {
  if (!reports || reports.length === 0) {
    return null;
  }

  const writeBackNotes = createBusniessOfficePostNotes(reports, comment);

  if (!writeBackNotes.comments || writeBackNotes.comments.length === 0) {
    return null;
  }

  const firstComment = writeBackNotes.comments[0];

  return {
    comments: writeBackNotes.comments,
    commentDate: firstComment.commentDate ?? "",
    type: firstComment.type ?? BusniessOfficeReportType.POST,
  };
};
